// src/components/customNotification/Notification.js
import React, { useEffect, useState, useRef } from "react";

const Notification = ({ message, type = "success", show, onClose, duration = 4000, title = null }) => {
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const [resettingProgress, setResettingProgress] = useState(false);
  const isDismissing = useRef(false);
  const lastEnqueuedKey = useRef(null);

  // ── Step 1: Enqueue whenever show is true and the message/type has changed
  useEffect(() => {
    if (!show) return;
    const triggerKey = `${message}__${type}__${title}`;
    if (triggerKey === lastEnqueuedKey.current) return;
    lastEnqueuedKey.current = triggerKey;
    setQueue((q) => [...q, { message, type, duration, title, id: Date.now() }]);
  }, [show, message, type, duration, title]);

  // ── Step 2: Pull next item from queue when nothing is showing
  useEffect(() => {
    if (!current && queue.length > 0 && !isDismissing.current) {
      const [next, ...rest] = queue;
      setQueue(rest);
      setCurrent(next);
    }
  }, [queue, current]);

  // ── Step 3: Animate in and auto-dismiss
  useEffect(() => {
    if (!current) return;

    isDismissing.current = false;
    setIsVisible(true);

    // Disable transition and snap to 100, then re-enable and animate to 0
    setResettingProgress(true);
    setProgress(100);
    const progressTimer = setTimeout(() => {
      setResettingProgress(false);
      setProgress(0);
    }, 50);
    const dismissTimer = setTimeout(() => dismiss(), current.duration);

    return () => {
      clearTimeout(progressTimer);
      clearTimeout(dismissTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  const dismiss = () => {
    isDismissing.current = true;
    setIsVisible(false);
    setTimeout(() => {
      setCurrent(null);
      isDismissing.current = false;
      onClose();
    }, 300);
  };

  if (!current) return null;

  const icons = {
    success: "✅",
    error: "❌",
    warning: "⚠️",
    info: "ℹ️"
  };

  const colorStyles = {
    success: {
      backgroundColor: "hsl(152, 81%, 96%)",
      borderLeftColor: "hsl(160, 84%, 39%)",
      color: "hsl(164, 86%, 17%)"
    },
    error: {
      backgroundColor: "hsl(0, 86%, 97%)",
      borderLeftColor: "hsl(0, 84%, 60%)",
      color: "hsl(0, 63%, 31%)"
    },
    warning: {
      backgroundColor: "hsl(48, 100%, 96%)",
      borderLeftColor: "hsl(38, 92%, 50%)",
      color: "hsl(22, 78%, 27%)"
    },
    info: {
      backgroundColor: "hsl(214, 100%, 97%)",
      borderLeftColor: "hsl(217, 91%, 60%)",
      color: "hsl(224, 64%, 33%)"
    }
  };

  const progressColors = {
    success: "hsl(160, 84%, 39%)",
    error: "hsl(0, 84%, 60%)",
    warning: "hsl(38, 92%, 50%)",
    info: "hsl(217, 91%, 60%)"
  };

  const currentType = current.type || "success";
  const currentDuration = current.duration || 4000;

  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        zIndex: 9999,
        transition: "all 0.3s ease-out",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(-10px)"
      }}
    >
      <div
        style={{
          width: "400px",
          borderRadius: "8px",
          borderLeft: "4px solid",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          ...colorStyles[currentType]
        }}
      >
        <div style={{ padding: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ flexShrink: 0, fontSize: "18px" }}>{icons[currentType]}</div>

            <div style={{ flex: 1 }}>
              {current.title && (
                <h4
                  style={{
                    fontWeight: 600,
                    fontSize: "14px",
                    margin: "0 0 4px 0",
                    lineHeight: "1.2"
                  }}
                >
                  {current.title}
                </h4>
              )}
              <div
                style={{
                  fontSize: "14px",
                  opacity: 0.9,
                  lineHeight: "1.4",
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  overflow: "visible"
                }}
              >
                {current.message}
              </div>
            </div>

            <button
              onClick={dismiss}
              style={{
                flexShrink: 0,
                opacity: 0.6,
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "18px",
                padding: "4px",
                transition: "opacity 0.2s",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              onMouseOver={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseOut={(e) => (e.currentTarget.style.opacity = "0.6")}
            >
              ✕
            </button>
          </div>
        </div>

        <div
          style={{
            height: "4px",
            backgroundColor: "rgba(0,0,0,0.1)",
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
            overflow: "hidden"
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              backgroundColor: progressColors[currentType],
              transition: resettingProgress ? "none" : `width ${currentDuration - 100}ms linear`,
              transformOrigin: "left center"
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Notification;
